import React, { memo, useState, useRef, useEffect } from 'react'
import { Handle, Position, NodeProps, NodeResizer, useReactFlow } from '@xyflow/react'
import RotateHandle from './RotateHandle'

/**
 * TextNode - Standalone text with no border/background (like draw.io text element)
 */
const TextNode = memo(({ data, selected, id }: NodeProps) => {
  const nodeData = data as any
  const { setNodes } = useReactFlow()
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState<string>(nodeData?.label || 'Text')
  const [isHovered, setIsHovered] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Focus when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.select()
    }
  }, [isEditing])

  // Update local value when label changes
  useEffect(() => {
    setEditValue(nodeData?.label || 'Text')
  }, [nodeData?.label])

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
  }

  const saveEdit = () => {
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== nodeData?.label) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, label: trimmed } }
            : node
        )
      )
    }
    setIsEditing(false)
  }

  const cancelEdit = () => {
    setEditValue(nodeData?.label || 'Text')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      saveEdit()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  // All text styling properties
  const textColor = nodeData.textColor || '#000000'
  const fontSize = nodeData.fontSize || 14
  const fontWeight = nodeData.fontWeight || '400'
  const fontFamily = nodeData.fontFamily || 'Inter, sans-serif'
  const textAlign = nodeData.textAlign || 'center'
  const verticalAlign = nodeData.verticalAlign || 'middle'
  const lineHeight = nodeData.lineHeight || 1.5
  const textDecoration = nodeData.textDecoration || 'none'
  const wordWrap = nodeData.wordWrap !== false
  const textOverflow = nodeData.textOverflow || 'clip'
  const textPadding = nodeData.textPadding !== undefined ? nodeData.textPadding : 4
  const letterSpacing = nodeData.letterSpacing || 0
  const textOpacity = nodeData.textOpacity !== undefined ? nodeData.textOpacity : 1
  const minWidth = nodeData.minWidth || 50
  const minHeight = nodeData.minHeight || 24
  const maxWidth = nodeData.maxWidth || 800
  const maxHeight = nodeData.maxHeight || 600

  // Fill/Stroke/Shadow properties
  const bgColor = nodeData.bgColor !== undefined ? nodeData.bgColor : 'transparent'
  const borderColor = nodeData.borderColor !== undefined ? nodeData.borderColor : 'transparent'
  const borderWidth = nodeData.borderWidth !== undefined ? nodeData.borderWidth : 0
  const borderStyle = nodeData.borderStyle || 'solid'
  const fillOpacity = nodeData.fillOpacity !== undefined ? nodeData.fillOpacity : 1
  const hasShadow = nodeData.shadow === true
  const shadowColor = nodeData.shadowColor || 'rgba(0, 0, 0, 0.2)'
  const shadowBlur = nodeData.shadowBlur || 4
  // Rotation (degrees) applied to the visible text content only.
  // NOTE: connection handles attach to the un-rotated bounding box.
  const rotation = nodeData.rotation || 0

  // Calculate box shadow
  const getBoxShadow = () => {
    if (selected) {
      const selectionShadow = '0 0 0 3px #f59e0b'
      if (hasShadow) {
        return `${selectionShadow}, 0 ${shadowBlur}px ${shadowBlur * 2}px ${shadowColor}`
      }
      return selectionShadow
    }
    if (hasShadow) {
      return `0 ${shadowBlur}px ${shadowBlur * 2}px ${shadowColor}`
    }
    return 'none'
  }

  const handleStyle = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#2563eb',
    border: '2px solid white',
    opacity: 0,
    transition: 'opacity 0.2s ease',
  }

  const showHandles = selected || isHovered

  return (
    <div
      className="text-node-wrapper"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: '100%',
        height: '100%',
        padding: `${textPadding}px`,
        minWidth: `${minWidth}px`,
        minHeight: `${minHeight}px`,
        display: 'flex',
        alignItems: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center',
        justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
        backgroundColor: bgColor,
        opacity: fillOpacity,
        border: `${borderWidth}px ${borderStyle} ${borderColor}`,
        borderRadius: '4px',
        boxShadow: getBoxShadow(),
        outline: selected ? '2px solid #3b82f6' : 'none',
        outlineOffset: '2px',
        cursor: isEditing ? 'text' : 'move',
        position: 'relative',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Resize handles */}
      <NodeResizer
        isVisible={selected}
        minWidth={minWidth}
        minHeight={minHeight}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        handleStyle={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#3b82f6',
          border: '2px solid white',
        }}
        lineStyle={{
          borderWidth: 1,
          borderColor: '#3b82f6',
        }}
      />

      {/* Drag-rotate handle (single selection) */}
      {selected && !isEditing && <RotateHandle nodeId={id} />}

      {isEditing ? (
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={handleKeyDown}
          onClick={(e) => e.stopPropagation()}
          style={{
            border: '2px solid #3b82f6',
            borderRadius: '4px',
            background: 'rgba(255, 255, 255, 0.95)',
            outline: 'none',
            resize: 'none',
            width: '100%',
            color: textColor,
            fontSize: `${fontSize}px`,
            fontWeight: fontWeight,
            fontFamily: fontFamily,
            textAlign: textAlign as any,
            lineHeight: lineHeight,
            letterSpacing: `${letterSpacing}px`,
            padding: '4px',
            overflow: 'auto',
          }}
          rows={editValue.split('\n').length || 1}
        />
      ) : (
        <div
          onDoubleClick={handleDoubleClick}
          style={{
            color: textColor,
            fontSize: `${fontSize}px`,
            fontWeight: fontWeight,
            fontFamily: fontFamily,
            textAlign: textAlign as any,
            lineHeight: lineHeight,
            textDecoration: textDecoration,
            letterSpacing: `${letterSpacing}px`,
            opacity: textOpacity,
            whiteSpace: wordWrap ? 'pre-wrap' : 'nowrap',
            wordBreak: wordWrap ? 'break-word' : 'normal',
            overflow: textOverflow === 'visible' ? 'visible' : textOverflow === 'clip' ? 'clip' : 'hidden',
            textOverflow: textOverflow === 'ellipsis' ? 'ellipsis' : 'clip',
            width: '100%',
            cursor: 'text',
            transform: rotation ? `rotate(${rotation}deg)` : undefined,
            transformOrigin: 'center',
          }}
        >
          {editValue}
        </div>
      )}

      {/* Connection handles - visible on selection or hover */}
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{ ...handleStyle, opacity: showHandles ? 1 : 0 }}
      />
      <Handle
        type="target"
        position={Position.Top}
        id="top-target"
        style={{ ...handleStyle, opacity: showHandles ? 1 : 0 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ ...handleStyle, opacity: showHandles ? 1 : 0 }}
      />
      <Handle
        type="target"
        position={Position.Right}
        id="right-target"
        style={{ ...handleStyle, opacity: showHandles ? 1 : 0 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ ...handleStyle, opacity: showHandles ? 1 : 0 }}
      />
      <Handle
        type="target"
        position={Position.Bottom}
        id="bottom-target"
        style={{ ...handleStyle, opacity: showHandles ? 1 : 0 }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{ ...handleStyle, opacity: showHandles ? 1 : 0 }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-target"
        style={{ ...handleStyle, opacity: showHandles ? 1 : 0 }}
      />
    </div>
  )
})

TextNode.displayName = 'TextNode'

export default TextNode
